'use client';

import type * as React from 'react';
import { Anchor, Building2, Search, XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import TaxonomyCombobox from '@/components/forms/TaxonomyCombobox';
import { buildTaxonomyIdLabelMap, useTaxonomyCategories } from '@/lib/taxonomy-service';

export type JobType = 'onboard' | 'shore';
export type JobCategory = 'all' | 'training';

export interface JobFiltersProps {
  jobType: JobType;
  onJobTypeChange: (type: JobType) => void;
  category: JobCategory;
  onCategoryChange: (value: JobCategory) => void;
  location: string;
  onLocationChange: (value: string) => void;
  rankId: string;
  onRankIdChange: (value: string) => void;
  vesselTypeId: string;
  onVesselTypeIdChange: (value: string) => void;
  shorePositionId: string;
  onShorePositionIdChange: (value: string) => void;
  className?: string;
}

export default function JobFilters({
  jobType,
  onJobTypeChange,
  category,
  onCategoryChange,
  location,
  onLocationChange,
  rankId,
  onRankIdChange,
  vesselTypeId,
  onVesselTypeIdChange,
  shorePositionId,
  onShorePositionIdChange,
  className,
}: JobFiltersProps) {
  const { departments, vesselCategories, shoreCategories } = useTaxonomyCategories(
    'departments',
    'vesselCategories',
    'shoreCategories'
  );
  const departmentLabels = buildTaxonomyIdLabelMap(departments);
  const vesselCategoryLabels = buildTaxonomyIdLabelMap(vesselCategories);
  const shoreCategoryLabels = buildTaxonomyIdLabelMap(shoreCategories);

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onJobTypeChange('onboard')}
          className={cn(
            'inline-flex min-h-11 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all',
            jobType === 'onboard'
              ? 'border-neon-cyan/30 bg-neon-cyan/10 text-neon-cyan'
              : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
          )}
        >
          <Anchor className="h-4 w-4" />
          Onboard Jobs
        </button>
        <button
          type="button"
          onClick={() => onJobTypeChange('shore')}
          className={cn(
            'inline-flex min-h-11 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all',
            jobType === 'shore'
              ? 'border-purple-400/30 bg-purple-400/10 text-purple-400'
              : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
          )}
        >
          <Building2 className="h-4 w-4" />
          Shore Jobs
        </button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Location..."
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            className="w-full min-h-11 rounded-lg border border-border bg-background py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>
        {jobType === 'onboard' ? (
          <>
            <FilterComboboxSlot value={rankId} onClear={() => onRankIdChange('')} label="Clear rank filter">
              <TaxonomyCombobox
                category="ranks"
                valueField="id"
                placeholder="All ranks"
                value={rankId || null}
                onChange={(nextValue) => onRankIdChange(nextValue ?? '')}
                groupByField="department"
                groupByLabelMap={departmentLabels}
                wrapperClassName="min-w-0 flex-1"
              />
            </FilterComboboxSlot>
            <FilterComboboxSlot value={vesselTypeId} onClear={() => onVesselTypeIdChange('')} label="Clear vessel type filter">
              <TaxonomyCombobox
                category="vesselTypes"
                valueField="id"
                placeholder="All vessel types"
                value={vesselTypeId || null}
                onChange={(nextValue) => onVesselTypeIdChange(nextValue ?? '')}
                groupByField="vesselCategory"
                groupByLabelMap={vesselCategoryLabels}
                wrapperClassName="min-w-0 flex-1"
              />
            </FilterComboboxSlot>
          </>
        ) : (
          <>
            <select
              value={category}
              onChange={(e) => onCategoryChange(e.target.value as JobCategory)}
              className="min-h-11 rounded-lg border border-border bg-background px-3 pr-10 text-sm text-foreground focus:border-primary focus:outline-none select-chevron"
              aria-label="Shore job category"
            >
              <option value="all">All shore positions</option>
              <option value="training">Training / Faculty</option>
            </select>
            <FilterComboboxSlot value={shorePositionId} onClear={() => onShorePositionIdChange('')} label="Clear shore position filter">
              <TaxonomyCombobox
                category="shorePositions"
                valueField="id"
                placeholder="All shore positions"
                value={shorePositionId || null}
                onChange={(nextValue) => onShorePositionIdChange(nextValue ?? '')}
                groupByField="shoreCategory"
                groupByLabelMap={shoreCategoryLabels}
                wrapperClassName="min-w-0 flex-1"
              />
            </FilterComboboxSlot>
          </>
        )}
      </div>
    </div>
  );
}

function FilterComboboxSlot({
  children,
  label,
  onClear,
  value,
}: {
  children: React.ReactNode;
  label: string;
  onClear: () => void;
  value: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 gap-2">
      {children}
      {value && (
        <button
          type="button"
          aria-label={label}
          onClick={onClear}
          className="min-h-11 rounded-lg border border-neon-cyan/10 bg-card px-3 text-ash transition hover:bg-panel hover:text-neon-cyan focus-visible:ring-2 focus-visible:ring-neon-cyan/50"
        >
          <XIcon aria-hidden="true" data-icon="inline-start" />
        </button>
      )}
    </div>
  );
}
